import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Copy, Cpu, MailPlus, Plus, Save, ShieldCheck, UserPlus, X } from 'lucide-react'
import { platformApi } from '@/lib/api/platform'
import { extractApiError, formatValidationError } from '@/lib/api'
import { Button, Input, LoadingSpinner } from '@/components/ui'
import type {
  BulkPlatformPerformanceResult,
  CreatePlatformInviteResult,
  PlatformInvite,
  PlatformPermissionCatalogItem,
  PlatformPerformanceResponse,
  PlatformTenant,
  PlatformUser,
  PlatformUserPermissions,
} from '@/types/platform'
import { useUIStore } from '@/stores/ui.store'

type PanelMode = 'empty' | 'create-tenant' | 'tenant-detail'

function fullInviteUrl(path: string) {
  const base = window.location.origin
  return path.startsWith('http') ? path : `${base}${path.startsWith('/') ? path : `/${path}`}`
}

function formatDate(value: string) {
  return new Date(value).toLocaleString('ru-RU')
}

function StatusBadge({ status }: { status: string }) {
  const active = status === 'active'
  return (
    <span
      className={
        active
          ? 'rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700'
          : 'rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700'
      }
    >
      {status}
    </span>
  )
}

function DetailField({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className={`mt-0.5 text-sm text-gray-900 ${mono ? 'font-mono text-xs break-all' : ''}`}>{value}</dd>
    </div>
  )
}

function BulkPerformanceBar({ tenantCount }: { tenantCount: number }) {
  const addToast = useUIStore((s) => s.addToast)
  const qc = useQueryClient()
  const [workers, setWorkers] = useState('4')
  const [formError, setFormError] = useState('')

  const mutation = useMutation({
    mutationFn: () => {
      const parsed = parseInt(workers, 10)
      if (!Number.isFinite(parsed) || parsed < 1) {
        return Promise.reject(new Error('Укажите число потоков от 1'))
      }
      return platformApi.setAllPerformance(parsed).then((res) => res.data as BulkPlatformPerformanceResult)
    },
    onSuccess: (body) => {
      setFormError('')
      void qc.invalidateQueries({ queryKey: ['platform-performance'] })
      const failHint = body.failed > 0 ? `, ошибок: ${body.failed}` : ''
      addToast(
        `Потоки рендера: ${body.max_workers_km} → обновлено кабинетов: ${body.updated}${failHint}`,
        body.failed > 0 ? 'warning' : 'success',
      )
    },
    onError: (err) => {
      setFormError(err instanceof Error ? err.message : formatValidationError(err) || extractApiError(err))
    },
  })

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-[220px]">
          <div className="rounded-lg bg-violet-50 p-2 text-violet-600">
            <Cpu className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Потоки рендера КМ — всем</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Применить одно значение ко всем кабинетам ({tenantCount})
            </p>
          </div>
        </div>
        <div>
          <label htmlFor="bulk-max-workers" className="mb-1 block text-xs font-medium text-gray-700">
            Потоков
          </label>
          <Input
            id="bulk-max-workers"
            type="number"
            min={1}
            max={64}
            value={workers}
            onChange={(e) => setWorkers(e.target.value)}
            className="w-24"
          />
        </div>
        <Button
          isLoading={mutation.isPending}
          disabled={tenantCount === 0}
          onClick={() => mutation.mutate()}
        >
          <Save className="mr-1 h-4 w-4" />
          Применить всем
        </Button>
      </div>
      {formError && (
        <p className="mt-2 text-sm text-red-600">{formError}</p>
      )}
    </div>
  )
}

function TenantPerformanceSection({ tenant }: { tenant: PlatformTenant }) {
  const addToast = useUIStore((s) => s.addToast)
  const [workers, setWorkers] = useState('')
  const [formError, setFormError] = useState('')

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['platform-performance', tenant.id],
    queryFn: async () => {
      const res = await platformApi.getPerformance(tenant.id)
      return res.data as PlatformPerformanceResponse
    },
  })

  useEffect(() => {
    if (data) {
      setWorkers(String(data.performance?.max_workers_km ?? 1))
      setFormError('')
    }
  }, [data, tenant.id])

  const mutation = useMutation({
    mutationFn: () => {
      const parsed = parseInt(workers, 10)
      if (!Number.isFinite(parsed) || parsed < 1) {
        return Promise.reject(new Error('Укажите число потоков от 1'))
      }
      return platformApi.setPerformance(tenant.id, parsed).then((res) => res.data as PlatformPerformanceResponse)
    },
    onSuccess: (body) => {
      setFormError('')
      setWorkers(String(body.performance?.max_workers_km ?? workers))
      addToast('Потоки рендера сохранены', 'success')
    },
    onError: (err) => {
      setFormError(err instanceof Error ? err.message : formatValidationError(err) || extractApiError(err))
    },
  })

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-center gap-2">
        <Cpu className="h-4 w-4 text-violet-600" />
        <h3 className="text-sm font-semibold text-gray-900">Потоки рендера КМ</h3>
      </div>
      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {extractApiError(error)}
          <Button size="sm" variant="ghost" className="mt-2" onClick={() => refetch()}>
            Повторить
          </Button>
        </div>
      ) : isLoading ? (
        <LoadingSpinner />
      ) : (
        <>
          {data?.cpu_count != null && (
            <p className="text-xs text-gray-500 mb-3">
              Ядер на сервере: <span className="font-medium text-gray-700">{data.cpu_count}</span>
            </p>
          )}
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <label htmlFor={`workers-${tenant.id}`} className="mb-1 block text-xs font-medium text-gray-700">
                Потоков
              </label>
              <Input
                id={`workers-${tenant.id}`}
                type="number"
                min={1}
                max={64}
                value={workers}
                onChange={(e) => setWorkers(e.target.value)}
                className="w-24"
              />
            </div>
            <Button size="sm" isLoading={mutation.isPending} onClick={() => mutation.mutate()}>
              <Save className="mr-1 h-4 w-4" />
              Сохранить
            </Button>
          </div>
          {formError && <p className="mt-2 text-sm text-red-600">{formError}</p>}
        </>
      )}
    </section>
  )
}

function CreateTenantPanel({
  onClose,
  onCreated,
}: {
  onClose: () => void
  onCreated: (tenant: PlatformTenant) => void
}) {
  const qc = useQueryClient()
  const addToast = useUIStore((s) => s.addToast)
  const [slug, setSlug] = useState('')
  const [name, setName] = useState('')
  const [formError, setFormError] = useState('')

  const createMutation = useMutation({
    mutationFn: () => platformApi.createTenant({ slug: slug.trim(), name: name.trim() }),
    onSuccess: (res) => {
      const body = res.data as { tenant: PlatformTenant }
      qc.invalidateQueries({ queryKey: ['platform-tenants'] })
      addToast('Кабинет создан', 'success')
      onCreated(body.tenant)
    },
    onError: (err) => setFormError(formatValidationError(err) || extractApiError(err)),
  })

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
        <h2 className="text-lg font-semibold text-gray-900">Новый кабинет</h2>
        <button type="button" onClick={onClose} className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="flex-1 space-y-4 overflow-y-auto p-5">
        {formError && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{formError}</div>
        )}
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Slug (латиница, a-z0-9-)</label>
          <Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="acme" />
          <p className="mt-1 text-xs text-gray-500">БД: truemark_t_{slug || '…'}</p>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Название</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="ООО Акме" />
        </div>
      </div>
      <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-4">
        <Button variant="ghost" onClick={onClose}>Отмена</Button>
        <Button isLoading={createMutation.isPending} onClick={() => createMutation.mutate()}>
          Создать
        </Button>
      </div>
    </div>
  )
}

function InviteUserSection({
  tenant,
  onInvited,
}: {
  tenant: PlatformTenant
  onInvited: () => void
}) {
  const qc = useQueryClient()
  const addToast = useUIStore((s) => s.addToast)
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<'admin' | 'worker'>('admin')
  const [formError, setFormError] = useState('')
  const [inviteResult, setInviteResult] = useState<CreatePlatformInviteResult | null>(null)

  const copyText = async (text: string) => {
    await navigator.clipboard.writeText(text)
    addToast('Скопировано', 'success')
  }

  const inviteMutation = useMutation({
    mutationFn: () =>
      platformApi.createInvite(tenant.id, { email: email.trim(), role }),
    onSuccess: (res) => {
      const body = res.data as CreatePlatformInviteResult & { success?: boolean }
      setInviteResult({
        invite: body.invite,
        invite_token: body.invite_token,
        invite_url: body.invite_url,
      })
      setEmail('')
      setFormError('')
      addToast('Приглашение создано', 'success')
      qc.invalidateQueries({ queryKey: ['platform-invites', tenant.id] })
      qc.invalidateQueries({ queryKey: ['platform-users', tenant.id] })
      onInvited()
    },
    onError: (err) => setFormError(formatValidationError(err) || extractApiError(err)),
  })

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
      <div className="mb-3 flex items-center gap-2">
        <UserPlus className="h-4 w-4 text-gray-500" />
        <h3 className="text-sm font-semibold text-gray-900">Пригласить пользователя</h3>
      </div>
      {formError && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{formError}</div>
      )}
      {inviteResult ? (
        <div className="space-y-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm">
          <p className="font-medium text-emerald-900">Приглашение для {inviteResult.invite.email}</p>
          <p className="text-gray-600">Действует до {formatDate(inviteResult.invite.expires_at)}</p>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" onClick={() => copyText(fullInviteUrl(inviteResult.invite_url))}>
              <Copy className="mr-1 h-4 w-4" />
              Ссылка
            </Button>
            <Button size="sm" variant="ghost" onClick={() => copyText(inviteResult.invite_token)}>
              Токен
            </Button>
          </div>
          <p className="break-all font-mono text-xs text-gray-700">{fullInviteUrl(inviteResult.invite_url)}</p>
          <Button size="sm" variant="ghost" onClick={() => setInviteResult(null)}>Ещё один инвайт</Button>
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Email</label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@company.ru"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Роль</label>
            <select
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={role}
              onChange={(e) => setRole(e.target.value as 'admin' | 'worker')}
            >
              <option value="admin">Администратор</option>
              <option value="worker">Работник</option>
            </select>
          </div>
          <Button
            size="sm"
            isLoading={inviteMutation.isPending}
            disabled={!email.trim()}
            onClick={() => inviteMutation.mutate()}
          >
            <MailPlus className="mr-1 h-4 w-4" />
            Создать инвайт
          </Button>
        </div>
      )}
    </div>
  )
}

function UserPermissionsEditor({
  tenant,
  user,
  onClose,
}: {
  tenant: PlatformTenant
  user: PlatformUser
  onClose: () => void
}) {
  const addToast = useUIStore((s) => s.addToast)
  const qc = useQueryClient()
  const [useDefaults, setUseDefaults] = useState(true)
  const [selected, setSelected] = useState<string[]>([])

  const { data: catalog = [], isLoading: catalogLoading } = useQuery({
    queryKey: ['platform-permissions-catalog', user.role],
    queryFn: async () => {
      const response = await platformApi.getPermissionsCatalog(user.role)
      return (response.data as { catalog: PlatformPermissionCatalogItem[] }).catalog
    },
  })
  const { data: permissions, isLoading: permissionsLoading } = useQuery({
    queryKey: ['platform-user-permissions', tenant.id, user.id],
    queryFn: async () => {
      const response = await platformApi.getUserPermissions(tenant.id, user.id)
      return (response.data as { permissions: PlatformUserPermissions }).permissions
    },
  })

  useEffect(() => {
    if (!permissions) return
    setUseDefaults(!permissions.permissions_configured)
    setSelected(
      permissions.permissions_configured
        ? permissions.allowed_endpoints ?? []
        : permissions.effective_endpoints,
    )
  }, [permissions])

  const mutation = useMutation({
    mutationFn: () =>
      platformApi.setUserPermissions(
        tenant.id,
        user.id,
        useDefaults ? null : selected,
      ),
    onSuccess: async () => {
      addToast(`Права ${user.email} сохранены`, 'success')
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['platform-users', tenant.id] }),
        qc.invalidateQueries({ queryKey: ['platform-user-permissions', tenant.id, user.id] }),
      ])
    },
    onError: (err) => addToast(formatValidationError(err) || extractApiError(err), 'error'),
  })

  const loading = catalogLoading || permissionsLoading

  return (
    <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50/40 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
            <ShieldCheck className="h-4 w-4 text-blue-600" />
            Права доступа: {user.email}
          </h4>
          <p className="mt-1 text-xs text-gray-500">
            Ограничение действует для sidebar, прямых ссылок и API разделов.
          </p>
        </div>
        <button type="button" onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-white">
          <X className="h-4 w-4" />
        </button>
      </div>

      {loading ? (
        <div className="py-5"><LoadingSpinner /></div>
      ) : (
        <>
          <label className="mt-4 flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={useDefaults}
              onChange={(event) => setUseDefaults(event.target.checked)}
            />
            Использовать права роли по умолчанию
          </label>

          <div className={`mt-3 grid gap-2 sm:grid-cols-2 ${useDefaults ? 'opacity-60' : ''}`}>
            {catalog.map((item) => (
              <label
                key={item.endpoint}
                className="flex items-start gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm"
              >
                <input
                  type="checkbox"
                  className="mt-0.5"
                  disabled={useDefaults}
                  checked={selected.includes(item.endpoint)}
                  onChange={(event) =>
                    setSelected((current) =>
                      event.target.checked
                        ? [...current, item.endpoint]
                        : current.filter((endpoint) => endpoint !== item.endpoint),
                    )
                  }
                />
                <span>
                  <span className="block font-medium text-gray-800">{item.label}</span>
                  <span className="block text-xs text-gray-400">{item.path}</span>
                </span>
              </label>
            ))}
          </div>

          <div className="mt-4 flex justify-end">
            <Button size="sm" isLoading={mutation.isPending} onClick={() => mutation.mutate()}>
              <Save className="mr-1 h-4 w-4" />
              Сохранить права
            </Button>
          </div>
        </>
      )}
    </div>
  )
}

function TenantDetailPanel({
  tenant,
  onClose,
}: {
  tenant: PlatformTenant
  onClose: () => void
}) {
  const [permissionsUser, setPermissionsUser] = useState<PlatformUser | null>(null)
  const {
    data: usersData,
    isLoading: usersLoading,
    error: usersError,
    refetch: refetchUsers,
  } = useQuery({
    queryKey: ['platform-users', tenant.id],
    queryFn: async () => {
      const res = await platformApi.listUsers(tenant.id)
      const body = res.data as { users?: PlatformUser[]; total?: number }
      return { users: body.users ?? [], total: body.total ?? body.users?.length ?? 0 }
    },
  })

  const {
    data: invitesData,
    isLoading: invitesLoading,
    error: invitesError,
    refetch: refetchInvites,
  } = useQuery({
    queryKey: ['platform-invites', tenant.id],
    queryFn: async () => {
      const res = await platformApi.listInvites(tenant.id)
      const body = res.data as { invites?: PlatformInvite[]; total?: number }
      return { invites: body.invites ?? [], total: body.total ?? body.invites?.length ?? 0 }
    },
  })

  const users = usersData?.users ?? []
  const invites = invitesData?.invites ?? []
  const pendingInvites = invites.filter((i) => !i.used_at)

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-start justify-between border-b border-gray-200 px-5 py-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{tenant.name}</h2>
          <p className="text-sm text-gray-500">{tenant.slug}</p>
        </div>
        <button type="button" onClick={onClose} className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="flex-1 space-y-5 overflow-y-auto p-5">
        <dl className="grid grid-cols-2 gap-4 rounded-lg border border-gray-200 bg-white p-4">
          <DetailField label="ID" value={tenant.id} mono />
          <DetailField label="Slug" value={tenant.slug} mono />
          <DetailField label="База данных" value={tenant.db_name} mono />
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Статус</dt>
            <dd className="mt-1"><StatusBadge status={tenant.status} /></dd>
          </div>
          <DetailField label="Создан" value={formatDate(tenant.created_at)} />
          <DetailField label="Обновлён" value={formatDate(tenant.updated_at)} />
        </dl>

        <TenantPerformanceSection key={tenant.id} tenant={tenant} />

        <section>
          <h3 className="mb-2 text-sm font-semibold text-gray-900">
            Пользователи ({usersLoading ? '…' : users.length})
          </h3>
          {usersError ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {extractApiError(usersError)}
              <Button size="sm" variant="ghost" className="mt-2" onClick={() => refetchUsers()}>
                Повторить
              </Button>
            </div>
          ) : usersLoading ? (
            <LoadingSpinner />
          ) : users.length === 0 ? (
            <p className="rounded-lg border border-dashed border-gray-200 bg-white p-4 text-sm text-gray-500">
              Пользователей пока нет — отправьте инвайт ниже
            </p>
          ) : (
            <div className="divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-200 bg-white">
              {users.map((u) => (
                <div key={u.id} className="flex items-start gap-2 px-3 py-2.5">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-900" title={u.email}>
                      {u.email}
                    </p>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <span className="text-xs text-gray-500">{u.role_name || u.role}</span>
                      <StatusBadge status={u.status} />
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="shrink-0"
                    onClick={() => setPermissionsUser(u)}
                  >
                    <ShieldCheck className="h-4 w-4" />
                    Права
                  </Button>
                </div>
              ))}
            </div>
          )}
          {permissionsUser && (
            <UserPermissionsEditor
              tenant={tenant}
              user={permissionsUser}
              onClose={() => setPermissionsUser(null)}
            />
          )}
        </section>

        <section>
          <h3 className="mb-2 text-sm font-semibold text-gray-900">
            Приглашения ({invitesLoading ? '…' : invites.length})
          </h3>
          {invitesError ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {extractApiError(invitesError)}
              <Button size="sm" variant="ghost" className="mt-2" onClick={() => refetchInvites()}>
                Повторить
              </Button>
            </div>
          ) : invitesLoading ? (
            <LoadingSpinner />
          ) : invites.length === 0 ? (
            <p className="rounded-lg border border-dashed border-gray-200 bg-white p-4 text-sm text-gray-500">
              Инвайтов пока нет
            </p>
          ) : (
            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
              <table className="w-full text-left text-sm">
                <thead className="bg-gray-50 text-gray-500">
                  <tr>
                    <th className="px-3 py-2 font-medium">Email</th>
                    <th className="px-3 py-2 font-medium">Роль</th>
                    <th className="px-3 py-2 font-medium">Статус</th>
                    <th className="px-3 py-2 font-medium">Истекает</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {invites.map((inv) => (
                    <tr key={inv.id}>
                      <td className="px-3 py-2 font-medium text-gray-900">{inv.email}</td>
                      <td className="px-3 py-2 text-gray-600">{inv.role}</td>
                      <td className="px-3 py-2">
                        {inv.used_at ? (
                          <span className="text-xs text-gray-500">использован</span>
                        ) : new Date(inv.expires_at) < new Date() ? (
                          <span className="text-xs text-red-600">истёк</span>
                        ) : (
                          <span className="text-xs text-amber-600">ожидает</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-500">{formatDate(inv.expires_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!invitesLoading && pendingInvites.length > 0 && (
            <p className="mt-2 text-xs text-gray-500">
              Активных инвайтов: {pendingInvites.filter((i) => new Date(i.expires_at) >= new Date()).length}
            </p>
          )}
        </section>

        <InviteUserSection tenant={tenant} onInvited={() => refetchInvites()} />
      </div>
    </div>
  )
}

export function PlatformTenantsPage() {
  const [panelMode, setPanelMode] = useState<PanelMode>('empty')
  const [selectedTenant, setSelectedTenant] = useState<PlatformTenant | null>(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['platform-tenants'],
    queryFn: async () => {
      const res = await platformApi.listTenants()
      const body = res.data as { tenants?: PlatformTenant[]; total?: number }
      return {
        tenants: body.tenants ?? [],
        total: body.total ?? body.tenants?.length ?? 0,
      }
    },
  })

  const openCreate = () => {
    setSelectedTenant(null)
    setPanelMode('create-tenant')
  }

  const openTenant = (t: PlatformTenant) => {
    setSelectedTenant(t)
    setPanelMode('tenant-detail')
  }

  const closePanel = () => {
    setPanelMode('empty')
    setSelectedTenant(null)
  }

  if (isLoading) return <LoadingSpinner />

  if (error) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
        {extractApiError(error)}
      </div>
    )
  }

  const tenants = data?.tenants ?? []

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Кабинеты</h1>
          <p className="text-sm text-gray-500">Всего: {data?.total ?? tenants.length}</p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Новый кабинет
        </Button>
      </div>

      <BulkPerformanceBar tenantCount={tenants.length} />

      <div className="grid gap-4 lg:grid-cols-[1fr_min(28rem,100%)]">
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-50 text-gray-500">
              <tr>
                <th className="px-4 py-3 font-medium">Название</th>
                <th className="px-4 py-3 font-medium">Slug</th>
                <th className="px-4 py-3 font-medium hidden md:table-cell">БД</th>
                <th className="px-4 py-3 font-medium">Статус</th>
                <th className="px-4 py-3 font-medium hidden sm:table-cell">Создан</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {tenants.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-gray-500">
                    Кабинетов пока нет — создайте первый
                  </td>
                </tr>
              ) : (
                tenants.map((t) => (
                  <tr
                    key={t.id}
                    onClick={() => openTenant(t)}
                    className={`cursor-pointer transition-colors hover:bg-gray-50 ${
                      selectedTenant?.id === t.id ? 'bg-blue-50/60' : ''
                    }`}
                  >
                    <td className="px-4 py-3 font-medium text-gray-900">{t.name}</td>
                    <td className="px-4 py-3 font-mono text-gray-600">{t.slug}</td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-400 hidden md:table-cell">{t.db_name}</td>
                    <td className="px-4 py-3"><StatusBadge status={t.status} /></td>
                    <td className="px-4 py-3 text-gray-500 hidden sm:table-cell">{formatDate(t.created_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="min-h-[480px] overflow-hidden rounded-xl border border-gray-200 bg-white lg:sticky lg:top-6 lg:self-start">
          {panelMode === 'create-tenant' && (
            <CreateTenantPanel
              onClose={closePanel}
              onCreated={(tenant) => {
                setSelectedTenant(tenant)
                setPanelMode('tenant-detail')
              }}
            />
          )}
          {panelMode === 'tenant-detail' && selectedTenant && (
            <TenantDetailPanel tenant={selectedTenant} onClose={closePanel} />
          )}
          {panelMode === 'empty' && (
            <div className="flex h-full min-h-[480px] flex-col items-center justify-center p-8 text-center">
              <p className="text-sm text-gray-500">
                Выберите кабинет в списке или создайте новый
              </p>
              <Button variant="secondary" size="sm" className="mt-4" onClick={openCreate}>
                <Plus className="mr-1 h-4 w-4" />
                Новый кабинет
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
